import {
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { Pagination, PaginationItem } from '@mui/material';
import SearchIcon from '@material-ui/icons/Search';
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import ActionMenuList from '../../../../shared/ActionMenuList/ActionMenuList';
import DeletePopupModel from '../../../../shared/delete-popup/Delete-Popup';
import ListFilterMenu from '../../../../shared/list-filter-menu/List-Filter-Menu';
import { notificationManager } from '../../../../utils/NotificationUtils';
import { fetchMobileDevices, deactivateGuestDevice } from '../../../../utils/devicesRestAPIUtils';
import { selectedHotelSelector } from '../../../property/propertySlice';
import {
  stepUpLoaderCount,
  stepDownLoaderCount,
} from '../../../../utils/loaderSlice';
import useDebounce from '../../../../custom-hooks/useDebounce';
import {
  MOBILE_DEVICE_FILTER_MENU,
  MOBILE_DEVICE_TYPE_PRIMARY,
  MOBILE_DEVICE_TYPE_SECONDARY,
} from '../../../../constants/DeviceConst';

const ArrowOnlyPagination = ({ count, rowsPerPage, page, onPageChange }) => {
  const numOfPages = Math.ceil(count / rowsPerPage);
  const handleChange = (event, value) => {
    onPageChange(event, value - 1);
  };
  return (
    <div>
      {numOfPages > 1 && (
        <Pagination
          page={page + 1}
          count={numOfPages}
          variant='outlined'
          shape='rounded'
          defaultPage={1}
          siblingCount={0}
          boundaryCount={0}
          onChange={handleChange}
          renderItem={(item) => {
            if (item.type === 'page' || item.type === 'start-ellipsis' || item.type === 'end-ellipsis') return null;
            return <PaginationItem {...item} />;
          }}
        />
      )}
    </div>
  );
};

const MobileDevicesList = () => {
  const dispatch = useDispatch();
  const selectedHotel = useSelector(selectedHotelSelector);

  const [devices, setDevices] = useState([]);
  const [expandedRows, setExpandedRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [nextToken, setNextToken] = useState(null);
  const [tokenMap, setTokenMap] = useState({ 0: '' });
  const [isLoading, setIsLoading] = useState(false);

  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 400);

  const [filterMenu, setFilterMenu] = useState(
    JSON.parse(JSON.stringify(MOBILE_DEVICE_FILTER_MENU)),
  );
  const [filterTargetButton, setFilterTargetButton] = useState(null);

  const [sortField, setSortField] = useState('ROOM_NO');
  const [sortDirection, setSortDirection] = useState('ASC');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);

  const [actionMenuItems, setActionMenuItems] = useState([]);
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [deletePopUp, setDeletePopUp] = useState({
    id: '',
    title: '',
    name: '',
    description: '',
    status: false,
    isBulk: false,
  });

  const fetchIdRef = useRef(0);

  const groupDevicesByReservation = (flatItems) => {
    if (!flatItems || flatItems.length === 0) return [];

    const isPrimary = (d) =>
      d.deviceType?.toLowerCase() === MOBILE_DEVICE_TYPE_PRIMARY.toLowerCase();
    const isSecondary = (d) =>
      d.deviceType?.toLowerCase() === MOBILE_DEVICE_TYPE_SECONDARY.toLowerCase();

    const primaryDevices = flatItems.filter(isPrimary);
    const secondaryDevices = flatItems.filter(isSecondary);

    const grouped = primaryDevices.map((parent) => {
      const children = secondaryDevices.filter(
        (child) =>
          child.reservationId &&
          parent.reservationId &&
          child.reservationId === parent.reservationId,
      );
      return { ...parent, children };
    });

    const matchedSecondaryIds = new Set(
      grouped.flatMap((g) => g.children.map((c) => c.deviceId)),
    );
    const unmatchedSecondary = secondaryDevices
      .filter((d) => !matchedSecondaryIds.has(d.deviceId))
      .map((d) => ({ ...d, children: [] }));

    const otherDevices = flatItems
      .filter((d) => !isPrimary(d) && !isSecondary(d))
      .map((d) => ({ ...d, children: [] }));

    const result = [...grouped, ...unmatchedSecondary, ...otherDevices];

    if (result.length === 0 && flatItems.length > 0) {
      return flatItems.map((d) => ({ ...d, children: [] }));
    }

    return result;
  };

  const activeDeviceType = useMemo(() => {
    const filter = filterMenu.find((f) => f.field === 'deviceType');
    if (!filter) return '';
    const selected = filter.subMenu.filter((s) => s.selected).map((s) => s.id);
    if (selected.length === 1) return selected[0];
    return '';
  }, [filterMenu]);

  const fetchDeviceData = useCallback(
    async (currentPage, currentToken) => {
      if (!selectedHotel) return;

      const fetchId = ++fetchIdRef.current;
      dispatch(stepUpLoaderCount());
      setIsLoading(true);

      try {
        const result = await fetchMobileDevices({
          hotelId: selectedHotel,
          deviceType: activeDeviceType,
          limit: rowsPerPage,
          nextToken: currentToken,
          search: debouncedSearch,
          sortBy: sortField,
          sortOrder: sortDirection,
        });

        if (fetchId !== fetchIdRef.current) return;

        const flatItems = (result.items || []).map((item) => ({
          ...item,
          isChecked: false,
        }));
        const grouped = groupDevicesByReservation(flatItems);
        setDevices(grouped);
        setExpandedRows([]);
        setNextToken(result.nextToken || null);
        if (result.nextToken) {
          setTokenMap((prev) => ({
            ...prev,
            [currentPage + 1]: result.nextToken,
          }));
        }
        setActionMenuItems([]);
      } catch (err) {
        if (fetchId !== fetchIdRef.current) return;
        notificationManager('danger', 'Failed to load devices');
      } finally {
        dispatch(stepDownLoaderCount());
        if (fetchId === fetchIdRef.current) {
          setIsLoading(false);
        }
      }
    },
    [
      selectedHotel,
      activeDeviceType,
      rowsPerPage,
      debouncedSearch,
      sortField,
      sortDirection,
      dispatch,
    ],
  );

  useEffect(() => {
    setPage(0);
    setTokenMap({ 0: '' });
    setNextToken(null);
    fetchDeviceData(0, '');
  }, [fetchDeviceData]);

  const filteredDevices = useMemo(() => {
    if (!debouncedSearch) return devices;
    const term = debouncedSearch.toLowerCase();
    const matchesSearch = (d) =>
      (d.deviceId && d.deviceId.toLowerCase().includes(term)) ||
      (d.deviceType && d.deviceType.toLowerCase().includes(term)) ||
      (d.reservationId && String(d.reservationId).toLowerCase().includes(term)) ||
      (d.roomNo && String(d.roomNo).toLowerCase().includes(term)) ||
      (d.guestName && d.guestName.toLowerCase().includes(term));

    return devices
      .map((parent) => {
        const matchingChildren = (parent.children || []).filter(matchesSearch);
        const parentMatches = matchesSearch(parent);
        if (parentMatches) return parent;
        if (matchingChildren.length > 0) return { ...parent, children: matchingChildren };
        return null;
      })
      .filter(Boolean);
  }, [devices, debouncedSearch]);

  const allFlatDevices = useMemo(() => {
    const flat = [];
    filteredDevices.forEach((parent) => {
      flat.push(parent);
      if (parent.children) {
        parent.children.forEach((child) => flat.push(child));
      }
    });
    return flat;
  }, [filteredDevices]);

  const totalCount = useMemo(() => {
    if (nextToken) {
      return (page + 2) * rowsPerPage;
    }
    return page * rowsPerPage + devices.length;
  }, [nextToken, page, rowsPerPage, devices.length]);

  const selectedCount = useMemo(() => {
    return allFlatDevices.filter((d) => d.isChecked).length;
  }, [allFlatDevices]);

  useEffect(() => {
    if (selectedCount > 0) {
      setActionMenuItems(['Delete']);
    } else {
      setActionMenuItems([]);
    }
  }, [selectedCount]);

  const handleCheckboxChange = (deviceId, parentDeviceId) => {
    setDevices((prev) => {
      if (parentDeviceId) {
        return prev.map((parent) => {
          if (parent.deviceId === parentDeviceId) {
            return {
              ...parent,
              children: (parent.children || []).map((child) =>
                child.deviceId === deviceId
                  ? { ...child, isChecked: !child.isChecked }
                  : child,
              ),
            };
          }
          return parent;
        });
      } else {
        return prev.map((d) => {
          if (d.deviceId === deviceId) {
            const newChecked = !d.isChecked;
            return {
              ...d,
              isChecked: newChecked,
              children: (d.children || []).map((c) => ({
                ...c,
                isChecked: newChecked,
              })),
            };
          }
          return d;
        });
      }
    });
  };

  const handleToggleExpand = (deviceId) => {
    setExpandedRows((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId],
    );
  };

  const handleSearchChange = (text) => {
    setSearchInput(text);
  };

  const handleFilterMenu = (event) => {
    setFilterTargetButton(event.currentTarget);
  };

  const handleFilter = (filterItems, field) => {
    if (field === 'all') {
      setFilterMenu(JSON.parse(JSON.stringify(MOBILE_DEVICE_FILTER_MENU)));
      return;
    }
    if (filterItems) {
      const updatedMenu = [...filterMenu];
      const idx = updatedMenu.findIndex((o) => o.field === field);
      if (idx !== -1) {
        updatedMenu[idx] = { ...updatedMenu[idx], subMenu: filterItems };
      }
      setFilterMenu(updatedMenu);
    }
  };

  const handleSortClick = (event) => {
    setSortAnchorEl(event.currentTarget);
  };

  const handleSortSelect = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
    } else {
      setSortField(field);
      setSortDirection('ASC');
    }
    setSortAnchorEl(null);
  };

  const handleActionMenuOpen = (event) => {
    setActionMenuAnchorEl(event.currentTarget);
  };

  const handleActionMenuClose = () => {
    setActionMenuAnchorEl(null);
  };

  const getDevicesToDelete = () => {
    const toDelete = [];
    devices.forEach((parent) => {
      if (parent.isChecked) {
        toDelete.push(parent);
      } else {
        (parent.children || []).forEach((child) => {
          if (child.isChecked) {
            toDelete.push(child);
          }
        });
      }
    });
    return toDelete;
  };

  const handleAction = (action) => {
    if (action === 'Delete') {
      let totalToDelete = 0;
      devices.forEach((parent) => {
        if (parent.isChecked) {
          totalToDelete += 1 + (parent.children || []).length;
        } else {
          (parent.children || []).forEach((child) => {
            if (child.isChecked) totalToDelete += 1;
          });
        }
      });

      setDeletePopUp({
        id: '',
        title: 'Are you sure you want to delete',
        name: `${totalToDelete} selected device${totalToDelete > 1 ? 's' : ''}`,
        description: '',
        status: true,
        isBulk: true,
      });
    }
  };

  const handleDeleteConfirm = async () => {
    const devicesToDelete = getDevicesToDelete();
    dispatch(stepUpLoaderCount());
    try {
      const results = await Promise.allSettled(
        devicesToDelete.map((device) => {
          const nameParts = (device.guestName || '').trim().split(' ');
          const firstName = nameParts[0] || '';
          const lastName = nameParts.slice(1).join(' ') || '';
          return deactivateGuestDevice({
            hotelId: selectedHotel,
            roomNo: device.roomNo || '',
            lastName,
            firstName,
            deviceId: device.deviceId,
          });
        }),
      );

      const failedCount = results.filter((r) => r.status === 'rejected').length;
      const successCount = results.filter((r) => r.status === 'fulfilled').length;

      if (successCount > 0) {
        notificationManager('success', `Device(s) deleted successfully`);
        fetchDeviceData(page, tokenMap[page] || '');
      }
      if (failedCount > 0) {
        notificationManager('danger', `${failedCount} device(s) failed to delete`);
      }
    } catch (err) {
      notificationManager('danger', 'Something went wrong. Please try again.');
    } finally {
      dispatch(stepDownLoaderCount());
      handleDeleteCancel();
    }
  };

  const handleDeleteCancel = () => {
    setDeletePopUp({
      id: '',
      title: '',
      name: '',
      description: '',
      status: false,
      isBulk: false,
    });
  };

  const handleChangePage = (event, newPage) => {
    const token = tokenMap[newPage] || '';
    setPage(newPage);
    fetchDeviceData(newPage, token);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const getSortLabel = () => {
    if (sortField === 'ROOM_NO') return ': Room No';
    if (sortField === 'GUEST_NAME') return ': Guest Name';
    return '';
  };

  return (
    <div className='mt-4 mb-4 group-list-container'>
      {deletePopUp.status && (
        <DeletePopupModel
          deletePopUp={deletePopUp}
          onDelete={handleDeleteConfirm}
          onDeleteCancel={handleDeleteCancel}
        />
      )}

      <div className='row mx-2 d-flex'>
        <div className='col-12 col-lg-4'>
          <TextField
            className='w-100'
            onChange={(e) => handleSearchChange(e.target.value)}
            label='Search'
            value={searchInput}
            InputProps={{
              endAdornment: (
                <InputAdornment position='end'>
                  <IconButton>
                    <SearchIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </div>
        <div className='col-12 col-lg-8 pt-3 pt-lg-0 d-flex justify-content-end align-items-center'>
          {actionMenuItems?.length > 0 && (
            <div className='me-2'>
              <Button
                onClick={handleActionMenuOpen}
                variant='outlined'
                size='large'
                color='secondary'
                endIcon={<ArrowDropDownIcon />}
                className='showpopup'
              >
                Actions
              </Button>
              <ActionMenuList
                anchorEl={actionMenuAnchorEl ?? null}
                handleClose={handleActionMenuClose}
                handleAction={handleAction}
                actionMenuItems={actionMenuItems}
              />
            </div>
          )}

          <Button
            variant='outlined'
            color='secondary'
            size='large'
            endIcon={<ArrowDropDownIcon />}
            onClick={handleSortClick}
            className='me-2 dropdown-button'
          >
            Sort{getSortLabel()}
            {sortDirection === 'ASC' ? (
              <ArrowUpwardIcon fontSize='small' className='ms-1' />
            ) : (
              <ArrowDownwardIcon fontSize='small' className='ms-1' />
            )}
          </Button>
          <Menu
            anchorEl={sortAnchorEl}
            open={Boolean(sortAnchorEl)}
            onClose={() => setSortAnchorEl(null)}
          >
            <MenuItem onClick={() => handleSortSelect('ROOM_NO')}>
              Room Number{' '}
              {sortField === 'ROOM_NO' &&
                (sortDirection === 'ASC' ? '↑' : '↓')}
            </MenuItem>
            <MenuItem onClick={() => handleSortSelect('GUEST_NAME')}>
              Guest Name{' '}
              {sortField === 'GUEST_NAME' &&
                (sortDirection === 'ASC' ? '↑' : '↓')}
            </MenuItem>
          </Menu>

          <Button
            variant='outlined'
            color='secondary'
            size='large'
            endIcon={<ArrowDropDownIcon />}
            onClick={handleFilterMenu}
            className={
              filterMenu?.some((f) => f?.subMenu?.some((s) => s?.selected))
                ? 'me-2 filterButton'
                : 'me-2 dropdown-button'
            }
          >
            Filter
          </Button>
          <ListFilterMenu
            menu={filterMenu}
            targetEl={filterTargetButton}
            handleFilterClose={() => setFilterTargetButton(null)}
            handleFilter={(items, field) => handleFilter(items, field)}
            hideSearch={false}
          />
        </div>
      </div>

      <div className='row mx-2 d-flex mt-4'>
          <TableContainer>
            <Table className='list-table' aria-label='mobile devices table'>
              <TableHead>
                <TableRow style={{ height: '52px' }}>
                  <TableCell style={{ width: '4%' }}></TableCell>
                  <TableCell style={{ width: '22%' }}>Device ID</TableCell>
                  <TableCell style={{ width: '12%' }}>Device Type</TableCell>
                  <TableCell style={{ width: '16%' }}>Reservation ID</TableCell>
                  <TableCell style={{ width: '14%' }}>Room Number</TableCell>
                  <TableCell style={{ width: '27%' }}>Guest Name</TableCell>
                  <TableCell style={{ width: '5%' }}></TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {filteredDevices.map((device) => (
                  <React.Fragment key={device.deviceId}>
                    <TableRow
                      className='border-white pointer'
                    >
                      <TableCell>
                        <Checkbox
                          onChange={() => handleCheckboxChange(device.deviceId)}
                          checked={device?.isChecked || false}
                          inputProps={{
                            'aria-label': `select device ${device.deviceId}`,
                          }}
                        />
                      </TableCell>
                      <TableCell>{device.deviceId}</TableCell>
                      <TableCell>
                        <span
                          className={
                            device.deviceType?.toLowerCase() === MOBILE_DEVICE_TYPE_PRIMARY.toLowerCase()
                              ? 'text-green'
                              : 'text-gray'
                          }
                        >
                          {device.deviceType}
                        </span>
                      </TableCell>
                      <TableCell>{device.reservationId}</TableCell>
                      <TableCell>{device.roomNo}</TableCell>
                      <TableCell>{device.guestName}</TableCell>
                      <TableCell style={{ textAlign: 'right' }}>
                        {device.children && device.children.length > 0 && (
                          <div
                            onClick={() => handleToggleExpand(device.deviceId)}
                            style={{ cursor: 'pointer', display: 'inline-flex' }}
                          >
                            {expandedRows.includes(device.deviceId) ? (
                              <ExpandLessIcon />
                            ) : (
                              <ExpandMoreIcon />
                            )}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                    {expandedRows.includes(device.deviceId) &&
                      device.children &&
                      device.children.map((child) => (
                        <TableRow
                          key={child.deviceId}
                          className='pointer'
                        >
                          <TableCell style={{ background: 'linear-gradient(to right, transparent 0.75rem, #f5f5f5 0.75rem)', borderBottom: 'none', borderTop: 'none' }}>
                            <Checkbox
                              onChange={() =>
                                handleCheckboxChange(child.deviceId, device.deviceId)
                              }
                              checked={child?.isChecked || false}
                              inputProps={{
                                'aria-label': `select device ${child.deviceId}`,
                              }}
                            />
                          </TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}>{child.deviceId}</TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}>
                            <span className='text-gray'>{child.deviceType}</span>
                          </TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}>{child.reservationId}</TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}>{child.roomNo}</TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}>{child.guestName}</TableCell>
                          <TableCell style={{ backgroundColor: '#f5f5f5', borderBottom: 'none', borderTop: 'none' }}></TableCell>
                        </TableRow>
                      ))}
                  </React.Fragment>
                ))}
              </TableBody>

              <TableFooter>
                <TableRow className='paginator'>
                  <TablePagination
                    rowsPerPageOptions={[10, 20, 30, 50]}
                    count={totalCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    labelDisplayedRows={() => ''}
                    SelectProps={{
                      inputProps: { 'aria-label': 'rows per page' },
                      native: true,
                    }}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    ActionsComponent={ArrowOnlyPagination}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
      </div>
    </div>
  );
};

export default MobileDevicesList;
